import React from 'react';
import PropTypes from 'prop-types';

class Collapsible extends React.PureComponent {
	constructor(props) {
		super(props);

		// Bind class methods
		this.handleTriggerClick = this.handleTriggerClick.bind(this);
		this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
		this.continueOpenCollapsible = this.continueOpenCollapsible.bind(this);
		this.setInnerRef = this.setInnerRef.bind(this);

		// Defaults the dropdown to be closed
		if (props.open) {
			this.state = {
				isClosed: false,
				shouldSwitchAutoOnNextCycle: false,
				height: 'auto',
				transition: 'none',
				hasBeenOpened: true,
				overflow: props.overflowWhenOpen,
				inTransition: false,
			};
		} else {
			this.state = {
				isClosed: true,
				shouldSwitchAutoOnNextCycle: false,
				height: 0,
				transition: `height ${props.transitionTime}ms ${props.easing}`,
				hasBeenOpened: false,
				overflow: 'hidden',
				inTransition: false,
			};
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state.shouldOpenOnNextCycle) {
			this.continueOpenCollapsible();
		}

		if (this.state.shouldSwitchAutoOnNextCycle === true) {
			window.setTimeout(() => { // Set small timeout to ensure a true re-render
				this.setState({
					height: 0,
					overflow: 'hidden',
					isClosed: true,
					shouldSwitchAutoOnNextCycle: false,
				});
			}, 50);
		}

		// If there has been a change in the open prop (controlled by accordion)
		if (prevProps.open !== this.props.open) {
			if(this.state.isClosed !== this.props.open) return;

			if (this.props.open === true) {
				this.openCollapsible();
				this.props.onOpening();
			} else {
				this.closeCollapsible();
				this.props.onClosing();
			}
		}
	}

	componentWillUnmount() {
		this.props.onUnmount(this);
	}

	closeCollapsible() {
		this.setState({
			shouldSwitchAutoOnNextCycle: true,
			height: this.innerRef.offsetHeight,
			transition: `height ${this.props.transitionCloseTime ?
				this.props.transitionCloseTime : this.props.transitionTime}ms ${this.props.easing}`,
			inTransition: true,
		});
	}

	openCollapsible() {
		this.setState({
			inTransition: true,
			shouldOpenOnNextCycle: true,
		});
	}

	continueOpenCollapsible() {
		this.setState({
			height: this.innerRef ? this.innerRef.offsetHeight : 0,
			transition: `height ${this.props.transitionTime}ms ${this.props.easing}`,
			isClosed: false,
			hasBeenOpened: true,
			inTransition: true,
			shouldOpenOnNextCycle: false,
		});
	}

	handleTriggerClick(event) {
		event.preventDefault();

		if (this.props.triggerDisabled) {
			return;
		}

		if (this.props.handleTriggerClick) {
			this.props.handleTriggerClick(this.props.accordionPosition);
		} else {
			if (this.state.isClosed === true) {
				this.openCollapsible();
				this.props.onOpening();
			} else {
				this.closeCollapsible();
				this.props.onClosing();
			}
		}
	}

	renderNonClickableTriggerElement() {
		if (this.props.triggerSibling && typeof this.props.triggerSibling === 'string') {
			return (
				<header className={`${this.props.classParentString}__trigger-sibling`}>{this.props.triggerSibling}</header>
			);
		} else if (this.props.triggerSibling) {
			return <this.props.triggerSibling/>;
		}

		return null;
	}

	handleTransitionEnd() {
		if(!this.state.inTransition) return;

		// Switch to height auto to make the container responsive
		if (!this.state.isClosed) {
			this.setState({height: 'auto', overflow: this.props.overflowWhenOpen, inTransition: false});
			this.props.onOpen();
		} else {
			this.setState({inTransition: false});
			this.props.onClose();
		}
	}

	setInnerRef(ref) {
		this.innerRef = ref;
	}

	render() {
		const dropdownStyle = {
			height: this.state.height,
			WebkitTransition: this.state.transition,
			msTransition: this.state.transition,
			transition: this.state.transition,
			overflow: this.state.overflow,
			visibility: this.state.isClosed ? "hidden" : "visible",
		};

		const openClass = this.state.isClosed ? 'is-closed' : 'is-open';
		const disabledClass = this.props.triggerDisabled ? 'is-disabled' : '';

		//If user wants different text when tray is open
		const trigger = (this.state.isClosed === false) && (this.props.triggerWhenOpen !== undefined)
			? this.props.triggerWhenOpen
			: this.props.trigger;

		// If user wants a trigger wrapping element different than 'header'
		const TriggerElement = this.props.triggerTagName;

		// Don't render children until the first opening of the Collapsible if lazy rendering is enabled
		const children = this.props.lazyRender
		&& !this.state.hasBeenOpened
		&& this.state.isClosed
		&& !this.state.inTransition ? null : this.props.children;

		// Construct CSS classes strings
		const triggerClassString = `${this.props.classParentString}__trigger ${openClass} ${disabledClass} ${
			this.state.isClosed ? this.props.triggerClassName : this.props.triggerOpenedClassName
			}`;
		const parentClassString = `${this.props.classParentString} ${
			this.state.isClosed ? this.props.className : this.props.openedClassName
			}`;
		const outerClassString = `${this.props.classParentString}__contentOuter ${this.props.contentOuterClassName}`;
		const innerClassString = `${this.props.classParentString}__contentInner ${this.props.contentInnerClassName}`;

		return (
			<section className={parentClassString.trim()} id={this.props.id}>
				<TriggerElement
					aria-labelledby={this.props.ariaLabelledById}
					className={triggerClassString.trim()}
					onClick={this.handleTriggerClick}
					style={this.props.triggerStyle && this.props.triggerStyle}
					onKeyPress={(event) => {
						const {key} = event;
						if (key === ' ' || key === 'Enter') {
							this.handleTriggerClick(event);
						}
					}}
					tabIndex={this.props.tabIndex && this.props.tabIndex}
				>
					{trigger}
				</TriggerElement>

				{this.renderNonClickableTriggerElement()}

				<div
					className={outerClassString.trim()}
					style={dropdownStyle}
					onTransitionEnd={this.handleTransitionEnd}
				>
					<div
						className={innerClassString.trim()}
						ref={this.setInnerRef}
					>
						{children}
					</div>
				</div>
			</section>
		);
	}
}

Collapsible.propTypes = {
	ariaLabelledById: PropTypes.string,
	children: PropTypes.any,  //eslint-disable-line react/forbid-prop-types
	transitionTime: PropTypes.number,
	transitionCloseTime: PropTypes.number,
	triggerTagName: PropTypes.string,
	easing: PropTypes.string,
	open: PropTypes.bool,
	className: PropTypes.string,
	classParentString: PropTypes.string,
	openedClassName: PropTypes.string,
	triggerStyle: PropTypes.object,
	triggerClassName: PropTypes.string,
	triggerOpenedClassName: PropTypes.string,
	contentOuterClassName: PropTypes.string,
	contentInnerClassName: PropTypes.string,
	accordionPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	handleTriggerClick: PropTypes.func,
	onOpen: PropTypes.func,
	onClose: PropTypes.func,
	onOpening: PropTypes.func,
	onClosing: PropTypes.func,
	onUnmount: PropTypes.func,
	trigger: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element
	]),
	triggerWhenOpen: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element
	]),
	triggerDisabled: PropTypes.bool,
	lazyRender: PropTypes.bool,
	overflowWhenOpen: PropTypes.oneOf([
		'hidden',
		'visible',
		'auto',
		'scroll',
		'inherit',
		'initial',
		'unset',
	]),
	triggerSibling: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.func,
	]),
	tabIndex: PropTypes.number,
	id: PropTypes.string
};

Collapsible.defaultProps = {
	transitionTime: 200,
	transitionCloseTime: null,
	triggerTagName: 'header',
	easing: 'linear',
	open: false,
	classParentString: 'Collapsible',
	triggerDisabled: false,
	lazyRender: false,
	overflowWhenOpen: 'hidden',
	openedClassName: '',
	triggerStyle: null,
	triggerClassName: '',
	triggerOpenedClassName: '',
	contentOuterClassName: '',
	contentInnerClassName: '',
	className: '',
	triggerSibling: null,
	onOpen: () => {
	},
	onClose: () => {
	},
	onOpening: () => {
	},
	onClosing: () => {
	},
	onUnmount: () => {},
	tabIndex: 0,
};

export default Collapsible;